import React, { useEffect, useMemo, useRef } from 'react';
import { useAssetContext } from '../../../context/AssetContext';
import { Color, Group, Mesh, ShaderMaterial } from 'three';
import SphereMaterial from '../materials/SphereMaterial';
import FocusPoints from '../../../data/FocusPoints';
import gsap from 'gsap';
import { useDetailViewContext } from '../../../context/DetailViewContext';
import { Colors } from '../../../data/Colors';
import { ThreeEvent } from '@react-three/fiber/dist/declarations/src/core/events';

const white = new Color(1, 1, 1);
const black = new Color(Colors.FraunhoferBlack);
const volumeColorLight = new Color(0.92, 0.95, 0.95);
const volumeColorDark = new Color(0.0, 0.05, 0.05);
const dimmedHighlight = new Color(0.2, 0.2, 0.2);

type Props = {
    outline?: number,
    color?: number,
    dottedAlpha?: number,
    hideShading?: boolean,
    onClick?: (event:ThreeEvent<MouseEvent>) => void
};


export default function VectorSphere({ outline, color, hideShading, dottedAlpha, onClick }: Props): JSX.Element
{
  const assets = useAssetContext();
  const geometry = useMemo(() =>
  {
    const gltf = assets.get('geoSphere') as Record<string, unknown>;
    const scene = gltf['scene'] as Group;
    const geometry = (scene.children[0] as Mesh).geometry;
    if (!geometry.userData.isScaled) {
      geometry.userData.isScaled = true;
      geometry.scale(1.07, 1.07, 1.07);
    }
    return geometry;
  }, [ assets ]);

  const materialRef = useRef<ShaderMaterial>(null);
  const animValue = useRef({ value: 0});
  const { activeFocusID } = useDetailViewContext();

  useEffect(() => {
    const mat = materialRef.current;
    if (!mat) return;
    const bgColor = mat.uniforms.fillColor.value;
    const volumeColor = mat.uniforms.volumeColor.value;
    const reflectionColor = mat.uniforms.reflectionColor.value;

    gsap.to(animValue.current, {
      value: activeFocusID === FocusPoints.None? 0 : 1,
      onUpdate: () => {
        const t = animValue.current.value;
        bgColor.lerpColors(white, black, t);
        volumeColor.lerpColors(volumeColorLight, volumeColorDark, t);
        reflectionColor.lerpColors(white, dimmedHighlight, t);
      }
    });
  }, [activeFocusID]);

  // We're padding the geometry a bit so the polys don't cut off the outlines
  return <mesh geometry={geometry} onClick={onClick} renderOrder={-10000}>
    <SphereMaterial color={color || 0x000000} outline={outline} hideShading={hideShading}
      dottedAlpha={dottedAlpha} ref={materialRef}/>
  </mesh>;
}